import {useCallback, useMemo} from 'react';

import {Typography} from '@mui/material';
import {Box} from '@mui/system';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import {PaymentLinkLogo} from '@/app/main/paymentLink/components/PaymentLinkLogo/PaymentLinkLogo';
import {AppButton, AppButtonVariant} from '@/app/shared-components';
import {InviterAvatar} from '@/app/shared-components/InviterAvatar/InviterAvatar';

import {
  GroupsViewModelProvider,
  useGroupsViewModelProvider,
} from './GroupsViewModelContext';

function GroupsErrorContent() {
  const {inviterName} = useGroupsViewModelProvider();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isNotFoundError = useMemo(
    () => location.state?.errorStatus === 404,
    [location.state?.errorStatus],
  );

  const title = useMemo(
    () =>
      isNotFoundError
        ? t('groups.error.not_found.title')
        : t('groups.error.unexpected.title'),
    [isNotFoundError, t],
  );

  const description = useMemo(
    () =>
      isNotFoundError
        ? t('groups.error.not_found.description')
        : t('groups.error.unexpected.description'),
    [isNotFoundError, t],
  );

  const handleTryAgain = useCallback(() => {
    navigate(`/groups?${searchParams.toString()}`, {replace: true});
  }, [navigate, searchParams]);

  return (
    <section
      className="splits-landing"
      style={{
        paddingLeft: 16,
        paddingRight: 16,
      }}>
      <Box className="splits-landing-container">
        <Box className="splits-landing-logo">
          <Box className="logo-container">
            <PaymentLinkLogo />
          </Box>
        </Box>
        <Box className="mt-24">
          <InviterAvatar inviterName={inviterName} />
        </Box>
        <Typography
          className="splits-confirmation-greeting mb-8"
          component="h1">
          {title}
        </Typography>
        <Typography className="splits-landing-description">
          {description}
        </Typography>

        {!isNotFoundError && (
          <Box className="mt-32 w-full">
            <AppButton
              id="splits-error-try-again"
              onClick={handleTryAgain}
              label={t('groups.error.unexpected.try_again')}
              variant={AppButtonVariant.onPrimary}
            />
          </Box>
        )}
      </Box>
    </section>
  );
}

const GroupsErrorPage = () => (
  <GroupsViewModelProvider>
    <GroupsErrorContent />
  </GroupsViewModelProvider>
);

export default GroupsErrorPage;
